import React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from 'react';

interface AIAssistantContextValue {
  /** Whether the AI Assistant side panel is open. */
  isOpen: boolean;
  /** The onboarding step the assistant is currently focused on, if any. */
  stepId: string | null;
  /** Open the assistant, optionally focusing a specific onboarding step. */
  openAIAssistant: (options?: { stepId?: string | null }) => void;
  /** Close the assistant and clear the focused step. */
  closeAIAssistant: () => void;
}

interface AIAssistantProviderProps {
  children: ReactNode;
  /** Initial open state. Defaults to closed. */
  defaultOpen?: boolean;
}

const AIAssistantContext = createContext<AIAssistantContextValue | undefined>(
  undefined
);

export function AIAssistantProvider({
  children,
  defaultOpen = false,
}: AIAssistantProviderProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [stepId, setStepId] = useState<string | null>(null);

  const openAIAssistant = useCallback(
    (options?: { stepId?: string | null }) => {
      setStepId(options?.stepId ?? null);
      setIsOpen(true);
    },
    []
  );

  const closeAIAssistant = useCallback(() => {
    setIsOpen(false);
    setStepId(null);
  }, []);

  const value = useMemo(
    () => ({ isOpen, stepId, openAIAssistant, closeAIAssistant }),
    [isOpen, stepId, openAIAssistant, closeAIAssistant]
  );

  return (
    <AIAssistantContext.Provider value={value}>
      {children}
    </AIAssistantContext.Provider>
  );
}

export const useAIAssistant = (): AIAssistantContextValue => {
  const context = useContext(AIAssistantContext);
  if (!context) {
    throw new Error('useAIAssistant must be used within AIAssistantProvider');
  }
  return context;
};
